/**
 * locationResolver.js — pure, React-free helpers for the brand-scoped
 * location (clinic) selection feature.
 *
 * WHY THIS FILE EXISTS: `Header.js` used to inline the brand→locations filter
 * AND fell back to `brandLocations[0]?.slug || 'atlanta'` whenever the URL had
 * no location, so a visitor in Raleigh browsing the Services mega-menu received
 * Atlanta service links. The resolution rules now live here (single source of
 * truth) so the Header, the header selector, and the first-visit gate all agree.
 *
 * Keep this module free of React imports — it is trivially unit-testable and is
 * also safe to require from Node scripts.
 */
import { extractLocaleFromPath } from "../i18n";
// Right-sized location data module (perf, 2026-08-18). The clinic list the
// header LocationSelector renders used to be sliced out of the 103KB
// taxonomy.json monolith at runtime. It now lives in its own ~7KB module whose
// `values` are byte-identical to taxonomy.dimensions[location].values, so the
// selector can resolve its list without walking the whole taxonomy object and
// the data can be preloaded independently (public/data/locations.json, linked
// rel=preload from the shell). taxonomy stays the source of truth for every
// other consumer; this module is generated from it and asserted equal in CI.
import locationsModule from "../data/locations.json";

const STORAGE_PREFIX = "apexosn:selectedLocation:";

/**
 * The canonical clinic list, sourced from the right-sized locations.json module
 * with a defensive fallback to the taxonomy object it was extracted from (so a
 * missing/empty module can never blank the selector). Contents are identical
 * either way - this only changes WHERE the list is read from, never WHAT it is.
 */
function allLocationValues(taxonomy) {
  const fromModule = locationsModule && Array.isArray(locationsModule.values)
    ? locationsModule.values
    : null;
  if (fromModule && fromModule.length) return fromModule;
  return (
    taxonomy?.dimensions?.find((d) => d.id === "location")?.values || []
  );
}

/**
 * Ordered list of locations a brand is allowed to surface.
 * Mirrors (and now replaces) the logic previously inlined in Header.js:
 * prefer `physicalSiteSlugs` in DECLARED order (TMM ground-truth physical
 * sites), else fall back to `brand.locations` / `l.brand === brandKey`.
 */
export function brandLocations(brand, taxonomy) {
  const locations = allLocationValues(taxonomy);
  if (!brand) return locations;
  const slugs = brand.physicalSiteSlugs;
  if (slugs && slugs.length) {
    return locations
      .filter((l) => slugs.includes(l.slug))
      .sort((a, b) => slugs.indexOf(a.slug) - slugs.indexOf(b.slug));
  }
  return locations.filter(
    (l) => brand.locations?.includes(l.slug) || l.brand === brand.brandKey
  );
}

/** True when `slug` is one of the brand's allowed locations. */
export function isAllowedLocation(slug, allowed) {
  if (!slug || !Array.isArray(allowed)) return false;
  return allowed.some((l) => l.slug === slug);
}

/**
 * Location slug carried by a URL path, or null.
 * Locale-prefix aware: /es/raleigh/back-pain-doctor → "raleigh".
 * Only the FIRST path segment is considered a location (matches the
 * /:locationSlug/:serviceSlug route shape), and it must be brand-allowed.
 */
export function locationFromPath(pathname, allowed) {
  if (!pathname) return null;
  const { pathWithoutLocale } = extractLocaleFromPath(pathname);
  const segments = pathWithoutLocale.split("/").filter(Boolean);
  if (!segments.length) return null;
  return isAllowedLocation(segments[0], allowed) ? segments[0] : null;
}

/** True when the current path is already scoped to a brand location. */
export function isLocationScopedPath(pathname, allowed) {
  return locationFromPath(pathname, allowed) !== null;
}

/** Service slug on a /:locationSlug/:serviceSlug path, else null. */
export function serviceFromPath(pathname, allowed) {
  if (!pathname) return null;
  const { pathWithoutLocale } = extractLocaleFromPath(pathname);
  const segments = pathWithoutLocale.split("/").filter(Boolean);
  if (segments.length !== 2) return null;
  return isAllowedLocation(segments[0], allowed) ? segments[1] : null;
}

const EARTH_RADIUS_MILES = 3958.7613;
const toRad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in miles between two lat/lng pairs (haversine). */
export function haversineMiles(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Nearest allowed location to a coordinate.
 * Returns { location, distanceMiles } or null when nothing is comparable.
 * lat/lng already exist on every taxonomy location — no geo dependency needed.
 */
export function nearestLocation(lat, lng, allowed) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  let best = null;
  (allowed || []).forEach((l) => {
    if (typeof l.lat !== "number" || typeof l.lng !== "number") return;
    const distanceMiles = haversineMiles(lat, lng, l.lat, l.lng);
    if (!best || distanceMiles < best.distanceMiles) {
      best = { location: l, distanceMiles };
    }
  });
  return best;
}

/** localStorage key, brand-scoped so the 3 domains never bleed into each other. */
export function storageKey(brandKey) {
  return `${STORAGE_PREFIX}${brandKey || "unknown"}`;
}

/**
 * Read a previously chosen slug, validated against the CURRENT brand's allowed
 * slugs (a stale gso slug must never apply on apexosn.com). Storage access is
 * guarded: private mode / disabled storage must not crash the app.
 */
export function readStoredLocation(brandKey, allowed) {
  try {
    const raw = window.localStorage.getItem(storageKey(brandKey));
    return isAllowedLocation(raw, allowed) ? raw : null;
  } catch (err) {
    return null;
  }
}

/** Persist a chosen slug (validated). Returns true when written. */
export function writeStoredLocation(brandKey, slug, allowed) {
  try {
    if (allowed && !isAllowedLocation(slug, allowed)) return false;
    if (!slug) {
      window.localStorage.removeItem(storageKey(brandKey));
      return true;
    }
    window.localStorage.setItem(storageKey(brandKey), slug);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * Physical-site record (label/addr1/city/state/zip/phone) matching a location,
 * used to show a real street address in the picker when the brand declares one.
 */
export function physicalSiteFor(brand, location) {
  if (!brand || !location) return null;
  const sites = brand.physicalSites || [];
  const city = String(location.city || "").toLowerCase();
  return (
    sites.find(
      (s) =>
        String(s.label || "").toLowerCase() === city ||
        (String(s.city || "").toLowerCase() === city &&
          String(s.state || "").toUpperCase() ===
            String(location.state || "").toUpperCase())
    ) || null
  );
}

/**
 * Full resolution precedence used to seed global state before first paint:
 * URL location (authoritative) → brand-scoped localStorage → null (ask).
 */
export function resolveInitialLocation(pathname, brandKey, allowed) {
  const fromPath = locationFromPath(pathname, allowed);
  if (fromPath) return fromPath;
  return readStoredLocation(brandKey, allowed);
}

/**
 * Does this visitor have to choose a clinic before they can proceed?
 *
 * The single source of truth for the MANDATORY LocationGate (2026-08-12). Pure
 * on purpose: the "can a visitor read a whole article with no clinic set" bug
 * was a decision-logic bug, so the decision is unit-tested rather than only
 * reasoned about in a component.
 *
 * True only when ALL of:
 *   - the brand actually has a choice to make (>1 allowed location) — a
 *     single-site brand must never be blocked, there is nothing to pick;
 *   - no clinic is selected yet (URL/localStorage seeding already happened in
 *     GlobalState, so `selectedSlug` null means genuinely unknown);
 *   - the current path is NOT location-scoped — `/raleigh/...` carries its own
 *     context, but `/blog/<post>`, `/services`, `/about-us` and `/` do not and
 *     must therefore block.
 */
export function requiresLocationSelection(pathname, selectedSlug, allowed) {
  if (!Array.isArray(allowed) || allowed.length < 2) return false;
  if (selectedSlug) return false;
  return !isLocationScopedPath(pathname, allowed);
}
